/**
 * useGoogleAdSense Hook
 *
 * Conditionally loads Google AdSense auto ads script only for non-premium users
 * This ensures premium users never see Google AdSense ads
 *
 * Features:
 * - Aggressive ad removal for premium users (including fullscreen overlays)
 * - Periodic checks to ensure ads stay removed
 * - URL hash cleanup for ad-related identifiers
 * - Waits for subscription status to load before showing ads to free users
 * - Timeout protection to prevent script hanging (5 seconds)
 * - Early premium check from localStorage to prevent loading
 */
import { useEffect, useRef, useState } from 'react';
import { useSubscription } from './useSubscription';
import { useAuth } from '../contexts/AuthContext';
import { logger } from "../utils/logger";
import { detectSubdomain } from '../utils/subdomainUtils';
import { getCachedDetection } from '../utils/headlessDetection';
import {
    ADSENSE_CLIENT_ID,
    ADSENSE_SCRIPT_ID,
    isAdRestrictedSurface,
    isViteAdSenseSuppressGoogleOverlaysEnabled,
} from '../../shared/adsense/adSurfacePolicy';
import { blockAdsbygoogleRemovable } from '../utils/applyAdFreeDomBlocking';
import { suppressGoogleOverlayAdsOnce } from '../ads/suppressGoogleOverlayAds';
// import { logger } from "../utils/logger";
const SCRIPT_LOAD_TIMEOUT = 5000; // 5 seconds
const EMERGENCY_CLEANUP_DURATION = 10000; // 10 seconds
const EMERGENCY_CLEANUP_INTERVAL = 500; // 500ms
export const useGoogleAdSense = (isAdFreeProp?: boolean, isServerBlockedProp?: boolean, isLoadingProp?: boolean) => {
    const subscription = useSubscription();
    const { isAuthenticated, isInitializing } = useAuth();
    const isAdFreeFromHook = subscription.isAdFree;
    const isLoadingFromHook = subscription.isLoading;
    const premiumStatus = subscription.premiumStatus;

    // AdSense is now CSP-compatible in production

    const isAdFree = isAdFreeProp !== undefined ? isAdFreeProp : isAdFreeFromHook;
    const isLoading = isLoadingProp !== undefined ? isLoadingProp : isLoadingFromHook;
    const isServerBlocked = isServerBlockedProp !== undefined ? isServerBlockedProp :
        (typeof document !== 'undefined' &&
            (document.documentElement.getAttribute('data-premium-user') === 'true' ||
                document.documentElement.getAttribute('data-adsense-blocked') === 'true' ||
                document.documentElement.getAttribute('data-k9-user') === 'true'));
    const cleanupIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const emergencyCleanupRef = useRef<NodeJS.Timeout | null>(null);
    const loadingRef = useRef<boolean>(false);
    const scriptTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const mutationObserverRef = useRef<MutationObserver | null>(null);
    const [isPageLoaded, setIsPageLoaded] = useState<boolean>(() => {
        if (typeof document === 'undefined') {
            return true;
        }
        return document.readyState === 'complete';
    });
    const [isIdleReady, setIsIdleReady] = useState<boolean>(false);

    useEffect(() => {
        if (isPageLoaded) {
            return;
        }
        const onWindowLoad = () => {
            setIsPageLoaded(true);
        };
        window.addEventListener('load', onWindowLoad, { once: true });
        return () => {
            window.removeEventListener('load', onWindowLoad);
        };
    }, [isPageLoaded]);

    useEffect(() => {
        if (!isPageLoaded || isIdleReady) {
            return;
        }
        let timerId: number | null = null;
        let idleId: number | null = null;
        const markReady = () => setIsIdleReady(true);
        if ('requestIdleCallback' in window) {
            idleId = (window as Window & { requestIdleCallback: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number }).requestIdleCallback(markReady, { timeout: 2000 });
        } else {
            timerId = window.setTimeout(markReady, 1200);
        }
        return () => {
            if (idleId !== null && 'cancelIdleCallback' in window) {
                (window as Window & { cancelIdleCallback: (handle: number) => void }).cancelIdleCallback(idleId);
            }
            if (timerId !== null) {
                window.clearTimeout(timerId);
            }
        };
    }, [isPageLoaded, isIdleReady]);
    // Log hook execution with current state
    useEffect(() => {
        logger.debug('[useGoogleAdSense] Hook executed', { "arg0": {
                isAdFree,
                isLoading,
                isServerBlocked,
                isAdFreeProp: isAdFreeProp !== undefined,
                premiumStatus: premiumStatus ? JSON.stringify(premiumStatus) : 'not loaded'
            } });
    }, [isAdFree, isLoading, isServerBlocked, isAdFreeProp, premiumStatus]);
    // Function to aggressively remove all Google ads
    // CRITICAL: Only removes ads for premium users, server-blocked, or forced
    const removeAllAds = (forceRemoval: boolean = false) => {
        // Check if user is a bot (for bot detection cleanup)
        const isBotBlocked = document.documentElement.getAttribute('data-bot-detected') === 'true' ||
                             document.documentElement.getAttribute('data-bot-completely-blocked') === 'true';
        
        // CRITICAL: Only remove ads if user is premium, server-blocked, or forced
        // This prevents removing ads for legitimate free users
        if (!isAdFree && !isServerBlocked && !isBotBlocked && !forceRemoval) {
            logger.debug('[useGoogleAdSense] ⏭️ Skipping ad removal - user is not premium, not server-blocked, and not forced', {
                isAdFree,
                isServerBlocked,
                isBotBlocked,
                forceRemoval
            });
            return 0; // Don't remove ads for legitimate free users
        }
        
        let removedCount = 0;
        const removalReason = isAdFree ? 'ad-free user' : isServerBlocked ? 'server-blocked' : isBotBlocked ? 'bot detected' : 'forced';

        const isEffectivelyEmptyElement = (el: Element): boolean =>
            el.children.length === 0 && !(el.textContent || '').trim();

        const pruneEmptyAdAncestors = (startParent: Element | null) => {
            let p = startParent;
            while (p && p !== document.body && p !== document.documentElement) {
                if (p.id === 'root') {
                    break;
                }
                if (isEffectivelyEmptyElement(p)) {
                    const gp = p.parentElement;
                    p.remove();
                    removedCount++;
                    p = gp;
                } else {
                    break;
                }
            }
        };

        // Remove AdSense script (check multiple possible IDs)
        const scriptIds = [ADSENSE_SCRIPT_ID, 'google-adsense', 'adsbygoogle-script'];
        scriptIds.forEach(id => {
            const script = document.getElementById(id);
            if (script) {
                script.remove();
                removedCount++;
                logger.debug(`[useGoogleAdSense] 🚫 AdSense script (${id}) removed for premium user`);
            }
        });
        // Remove all scripts that load Google AdSense
        const allScripts = document.querySelectorAll('script[src*="googlesyndication"], script[src*="adsbygoogle"], script[src*="doubleclick"]');
        allScripts.forEach(script => {
            script.remove();
            removedCount++;
        });
        // Remove Funding Choices scripts and elements
        const fundingChoicesScripts = document.querySelectorAll('script[src*="fundingchoicesmessages"], script[data-funding-choices]');
        fundingChoicesScripts.forEach(script => {
            script.remove();
            removedCount++;
        });
        // Remove Funding Choices iframe
        const fundingChoicesIframe = document.querySelector('iframe[name="googlefcPresent"]');
        if (fundingChoicesIframe) {
            fundingChoicesIframe.remove();
            removedCount++;
        }
        // Remove Funding Choices modal/overlay elements
        const fundingChoicesOverlays = document.querySelectorAll('[id*="googlefc"], [class*="googlefc"], [id*="funding-choices"]');
        fundingChoicesOverlays.forEach(overlay => {
            overlay.remove();
            removedCount++;
        });
        // Remove all AdSense ad units (multiple selectors)
        const adSelectors = [
            'ins.adsbygoogle',
            '[data-ad-slot]',
            '[data-ad-client]',
            '[id*="google_ads"]',
            '[id*="goog-"]',
            '[id*="aswift_"]',
            '[class*="adsbygoogle"]',
            '[class*="google-auto-placed"]',
            '[class*="google-ads"]'
        ];
        adSelectors.forEach(selector => {
            try {
                const ads = document.querySelectorAll(selector);
                ads.forEach(ad => {
                    ad.remove();
                    removedCount++;
                });
            }
            catch (e) {
                // Ignore invalid selectors
            }
        });
        // Remove Google fullscreen ad overlays and paywall popups
        const fullscreenOverlays = document.querySelectorAll('[id*="google_ads"], [id*="goog-"], [id*="aswift_"], [class*="adsbygoogle"], [class*="google-auto-placed"], [class*="google-ads"]');
        fullscreenOverlays.forEach(overlay => {
            if (overlay.parentElement) {
                overlay.remove();
                removedCount++;
            }
        });
        // Remove any fullscreen ad backdrop/containers (including paywall popups)
        const adContainers = document.querySelectorAll('div[style*="position: fixed"], div[style*="position:fixed"]');
        adContainers.forEach(container => {
            const html = container.innerHTML.toLowerCase();
            const id = container.id?.toLowerCase() || '';
            const className = container.className?.toLowerCase() || '';
            const hasAdContent = html.includes('google') ||
                html.includes('adsbygoogle') ||
                html.includes('pagead') ||
                html.includes('doubleclick') ||
                html.includes('googlesyndication') ||
                id.includes('google') ||
                id.includes('ads') ||
                id.includes('goog') ||
                className.includes('ads') ||
                className.includes('google');
            // Also check for overlay characteristics (high z-index, covers screen)
            const style = window.getComputedStyle(container);
            const isOverlay = style.position === 'fixed' &&
                (parseInt(style.zIndex) > 1000 || style.zIndex === 'auto') &&
                hasAdContent;
            if (hasAdContent || isOverlay) {
                container.remove();
                removedCount++;
            }
        });
        // Remove iframes that might contain ads or paywalls (more comprehensive)
        const iframeSelectors = [
            'iframe[id*="google"]',
            'iframe[id*="ads"]',
            'iframe[id*="goog"]',
            'iframe[id*="aswift"]',
            'iframe[src*="googlesyndication"]',
            'iframe[src*="doubleclick"]',
            'iframe[src*="googleadservices"]',
            'iframe[src*="pagead"]'
        ];
        iframeSelectors.forEach(selector => {
            try {
                const iframes = document.querySelectorAll(selector);
                iframes.forEach(iframe => {
                    iframe.remove();
                    removedCount++;
                });
            }
            catch (e) {
                // Ignore invalid selectors
            }
        });
        // Remove any elements with Google AdSense data attributes
        const dataAdElements = document.querySelectorAll('[data-ad-client], [data-ad-slot], [data-ad-format]');
        dataAdElements.forEach(element => {
            element.remove();
            removedCount++;
        });
        // Collapse empty wrappers left after ad units were stripped (avoids tall blank gaps in document flow)
        const emptyAdWrapperSelectors = [
            '[class*="google-auto-placed"]',
            '[class*="google-auto-placed-app-container"]',
            'ins.adsbygoogle',
            '[id*="google_ads"]',
            '[id*="aswift_"]',
            '[id*="goog-"]',
            '[class*="google-ads"]',
            '[data-anchor-status]',
            '[data-google-query-id]',
        ];
        try {
            emptyAdWrapperSelectors.forEach((selector) => {
                document.querySelectorAll(selector).forEach((el) => {
                    if (!isEffectivelyEmptyElement(el)) {
                        return;
                    }
                    const parent = el.parentElement;
                    el.remove();
                    removedCount++;
                    pruneEmptyAdAncestors(parent);
                });
            });
        }
        catch {
            // Ignore selector issues
        }
        // Remove any backdrop/overlay elements that might be left behind
        const backdrops = document.querySelectorAll('[class*="backdrop"], [id*="backdrop"]');
        backdrops.forEach(backdrop => {
            const style = window.getComputedStyle(backdrop);
            if (style.position === 'fixed' && (style.backgroundColor.includes('rgba') || style.opacity !== '1')) {
                backdrop.remove();
                removedCount++;
            }
        });
        // Remove any SVG elements that might be ad-related (from the original error)
        const svgAds = document.querySelectorAll('svg[width="auto"], svg[height="auto"]');
        svgAds.forEach(svg => {
            const parent = svg.closest('[id*="google"], [id*="ads"], [class*="ads"]');
            if (parent) {
                parent.remove();
                removedCount++;
            }
        });
        // Clear the URL hash if it contains ad-related identifiers
        if (window.location.hash.includes('goog_') || window.location.hash.includes('_ad')) {
            history.replaceState(null, '', window.location.pathname + window.location.search);
            logger.debug('[useGoogleAdSense] 🚫 Cleared ad-related URL hash for premium user');
        }
        // Restore body scroll if ads disabled it
        if (document.body.style.overflow === 'hidden') {
            const hasNonAdModal = document.querySelector('.MuiDialog-root, .MuiModal-root');
            if (!hasNonAdModal) {
                document.body.style.overflow = '';
                removedCount++;
            }
        }
        // Disable Google AdSense if it's already loaded
        if (window.adsbygoogle) {
            try {
                // Prevent adsbygoogle from running
                window.adsbygoogle = undefined;
                removedCount++;
            }
            catch (e) {
                // Ignore errors
            }
        }
        if (removedCount > 0) {
            logger.debug(`[useGoogleAdSense] 🚫 Removed ${removedCount} ad elements`, {
                removedCount,
                removalReason,
                isAdFree,
                isServerBlocked,
                isBotBlocked,
                premiumStatus: premiumStatus ? JSON.stringify(premiumStatus) : 'not loaded'
            });
        }
        return removedCount;
    };
    useEffect(() => {
        // EARLY CHECK: Check localStorage for premium status to prevent any loading attempts
        const cachedPremiumStatus = localStorage.getItem('premiumStatus');
        const isPremiumCached = cachedPremiumStatus === 'true';
        const cachedShowAds = localStorage.getItem('adEligibilityShowAds');
        const isAdFreeCached = cachedShowAds === 'false';

        const serverSideBlocked =
            document.documentElement.getAttribute('data-premium-user') === 'true' ||
            document.documentElement.getAttribute('data-adsense-blocked') === 'true' ||
            document.documentElement.getAttribute('data-k9-user') === 'true';

        const shouldBlockEarly =
            serverSideBlocked ||
            isServerBlocked ||
            isAdFree ||
            isAdFreeCached;

        logger.debug('[useGoogleAdSense] Early blocking check', {
            isAdFree,
            isPremiumCached,
            isAdFreeCached,
            isServerBlocked,
            serverSideBlocked,
            shouldBlockEarly,
            cachedPremiumStatus,
            cachedShowAds,
        });
        
        // Block AdSense on restricted subdomains and routes (platform surfaces)
        const subdomainInfo = detectSubdomain();
        if (isAdRestrictedSurface(window.location.pathname, subdomainInfo.subdomain)) {
            logger.debug('[useGoogleAdSense] 🚫 Ads disabled on platform surface', {
                subdomain: subdomainInfo.subdomain,
                path: window.location.pathname
            });
            removeAllAds(true);
            return;
        }

        // CRITICAL: Block AdSense script loading immediately if premium
        // This must happen BEFORE the script can load
        if (shouldBlockEarly) {
            // Block any existing AdSense scripts from executing
            const blockAdSense = () => {
                // Remove any AdSense scripts that might be loading
                const scripts = document.querySelectorAll('script[src*="googlesyndication"], script[src*="adsbygoogle"]');
                scripts.forEach(script => script.remove());
                // Block window.adsbygoogle (removable — configurable:true)
                if (typeof window !== 'undefined') {
                    try {
                        blockAdsbygoogleRemovable();
                        logger.debug('[useGoogleAdSense] ✅ Blocked window.adsbygoogle for ad-free user (removable)');
                    } catch (e) {
                        logger.warn('[useGoogleAdSense] ⚠️ Could not block window.adsbygoogle:', e);
                    }
                }
            };
            // Run immediately
            blockAdSense();
            // Also block on script load events
            document.addEventListener('DOMContentLoaded', blockAdSense);
        } else {
            logger.debug('[useGoogleAdSense] ✅ Ads allowed - NOT blocking AdSense early', {
                isAdFree,
                isAdFreeCached,
                isServerBlocked,
                serverSideBlocked,
            });
        }
        // CRITICAL: If user has premium (from hook or cache), aggressively remove all ads immediately
        // Do this BEFORE checking isLoading to prevent any race conditions
        if (isAdFree || isAdFreeCached || isServerBlocked) {
            logger.debug('[useGoogleAdSense] ✅ Ad-free user detected - removing all Google ads', { "arg0": {
                    isAdFree,
                    isAdFreeCached,
                    isServerBlocked,
                    premiumStatus: premiumStatus ? JSON.stringify(premiumStatus) : 'not loaded'
                } });
            // Generic block flags only; reason-specific attrs (premium vs K9) come from applyAdFreeDomBlocking
            document.documentElement.setAttribute('data-adsense-blocked', 'true');
            document.documentElement.setAttribute('data-funding-choices-blocked', 'true');
            // Remove ads immediately
            removeAllAds();
            // Set up periodic cleanup to catch any dynamically injected ads (more frequent)
            cleanupIntervalRef.current = setInterval(() => {
                const removed = removeAllAds();
                if (removed > 0) {
                    logger.warn('[useGoogleAdSense] ⚠️ Found and removed', { "arg0": removed, "arg1": 'ads that were injected after initial cleanup' });
                }
            }, 1000); // Check every 1 second for premium users
            logger.debug('[useGoogleAdSense] ✅ Set up periodic cleanup for premium user');
            // Emergency cleanup - more aggressive for first 10 seconds
            let emergencyCleanupCount = 0;
            const maxEmergencyCleanups = EMERGENCY_CLEANUP_DURATION / EMERGENCY_CLEANUP_INTERVAL;
            emergencyCleanupRef.current = setInterval(() => {
                emergencyCleanupCount++;
                const removed = removeAllAds();
                if (removed > 0) {
                    logger.warn('[useGoogleAdSense] 🚨 Emergency cleanup removed ads for premium user');
                }
                // Stop emergency cleanup after 10 seconds
                if (emergencyCleanupCount >= maxEmergencyCleanups && emergencyCleanupRef.current) {
                    clearInterval(emergencyCleanupRef.current);
                    emergencyCleanupRef.current = null;
                    logger.debug('[useGoogleAdSense] ✅ Emergency cleanup completed');
                }
            }, EMERGENCY_CLEANUP_INTERVAL);
            // Set up MutationObserver to catch ads being dynamically injected
            // This prevents race conditions when ads try to reload after popup dismissal
            mutationObserverRef.current = new MutationObserver((mutations) => {
                let shouldClean = false;
                for (const mutation of mutations) {
                    if (mutation.type === 'childList') {
                        mutation.addedNodes.forEach((node) => {
                            if (node.nodeType === Node.ELEMENT_NODE) {
                                const element = node as Element;
                                const id = element.id?.toLowerCase() || '';
                                const className = element.className?.toString().toLowerCase() || '';
                                const tagName = element.tagName?.toLowerCase() || '';
                                // Check if added node is an ad-related element (more comprehensive)
                                const isAdElement = className.includes('adsbygoogle') ||
                                    className.includes('google-auto-placed') ||
                                    className.includes('google-ads') ||
                                    className.includes('googlefc') ||
                                    id.includes('google_ads') ||
                                    id.includes('goog-') ||
                                    id.includes('aswift_') ||
                                    id.includes('googlefc') ||
                                    id.includes('funding-choices') ||
                                    id.includes('google') && id.includes('ads') ||
                                    (tagName === 'ins' && className.includes('adsbygoogle')) ||
                                    (tagName === 'script' && ((element as HTMLScriptElement).src?.includes('googlesyndication') ||
                                        (element as HTMLScriptElement).src?.includes('adsbygoogle') ||
                                        (element as HTMLScriptElement).src?.includes('doubleclick') ||
                                        (element as HTMLScriptElement).src?.includes('fundingchoicesmessages') ||
                                        element.hasAttribute('data-funding-choices'))) ||
                                    (tagName === 'iframe' && (id.includes('google') ||
                                        id.includes('ads') ||
                                        id.includes('goog') ||
                                        id.includes('aswift') ||
                                        id.includes('googlefc') ||
                                        (element as HTMLIFrameElement).name === 'googlefcPresent' ||
                                        (element as HTMLIFrameElement).src?.includes('googlesyndication') ||
                                        (element as HTMLIFrameElement).src?.includes('doubleclick') ||
                                        (element as HTMLIFrameElement).src?.includes('googleadservices') ||
                                        (element as HTMLIFrameElement).src?.includes('pagead') ||
                                        (element as HTMLIFrameElement).src?.includes('fundingchoicesmessages'))) ||
                                    element.hasAttribute('data-ad-client') ||
                                    element.hasAttribute('data-ad-slot') ||
                                    element.hasAttribute('data-ad-format');
                                if (isAdElement) {
                                    // CRITICAL: Only remove if user is premium or server-blocked
                                    // This prevents removing legitimate AdSense elements for free users
                                    if (isAdFree || isServerBlocked) {
                                        logger.warn('[useGoogleAdSense] 🚨 MutationObserver detected ad injection attempt - removing immediately', { "arg0": {
                                                tagName,
                                                id: id.substring(0, 50),
                                                className: className.substring(0, 50),
                                                isAdFree,
                                                isServerBlocked
                                            } });
                                        element.remove();
                                        shouldClean = true;
                                    } else {
                                        logger.debug('[useGoogleAdSense] ⏭️ MutationObserver detected ad element but user is not premium - allowing', {
                                            tagName,
                                            id: id.substring(0, 50),
                                            isAdFree,
                                            isServerBlocked
                                        });
                                    }
                                }
                            }
                            else if (node.nodeType === Node.TEXT_NODE) {
                                // Check if text node contains ad-related content
                                const text = node.textContent?.toLowerCase() || '';
                                if (text.includes('adsbygoogle') || text.includes('googlesyndication')) {
                                    shouldClean = true;
                                }
                            }
                        });
                    }
                    else if (mutation.type === 'attributes') {
                        // Watch for attribute changes that might indicate ad loading
                        const target = mutation.target as Element;
                        const attrName = mutation.attributeName?.toLowerCase() || '';
                        if (attrName === 'src' || attrName === 'id' || attrName === 'class') {
                            const src = (target as HTMLElement).getAttribute('src')?.toLowerCase() || '';
                            const id = target.id?.toLowerCase() || '';
                            const className = target.className?.toString().toLowerCase() || '';
                            if (src.includes('googlesyndication') ||
                                src.includes('doubleclick') ||
                                src.includes('adsbygoogle') ||
                                id.includes('google_ads') ||
                                id.includes('goog-') ||
                                className.includes('adsbygoogle')) {
                                // CRITICAL: Only remove if user is premium or server-blocked
                                if (isAdFree || isServerBlocked) {
                                    logger.warn('[useGoogleAdSense] 🚨 MutationObserver detected ad attribute change - removing', {
                                        isAdFree,
                                        isServerBlocked
                                    });
                                    target.remove();
                                    shouldClean = true;
                                } else {
                                    logger.debug('[useGoogleAdSense] ⏭️ MutationObserver detected ad attribute change but user is not premium - allowing', {
                                        isAdFree,
                                        isServerBlocked
                                    });
                                }
                            }
                        }
                    }
                }
                if (shouldClean) {
                    // CRITICAL: Only do full cleanup if user is premium or server-blocked
                    // removeAllAds() already has this check, but adding here for clarity
                    if (isAdFree || isServerBlocked) {
                        // Do a full cleanup to catch anything the observer missed
                        removeAllAds();
                    }
                }
            });
            // Observe the entire document for ad injections (including attribute changes)
            mutationObserverRef.current.observe(document.body, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['src', 'id', 'class', 'data-ad-client', 'data-ad-slot'],
                characterData: false
            });
            logger.debug('[useGoogleAdSense] ✅ MutationObserver active - monitoring for ad injection attempts');
            // Also set up event listeners to catch ads on user interactions
            const handleInteraction = () => {
                const removed = removeAllAds();
                if (removed > 0) {
                    logger.warn('[useGoogleAdSense] ⚠️ Removed ads detected during user interaction');
                }
            };
            // Listen for various events that might trigger ad loading
            document.addEventListener('scroll', handleInteraction, { passive: true });
            document.addEventListener('click', handleInteraction, { passive: true });
            document.addEventListener('mousemove', handleInteraction, { passive: true });
            window.addEventListener('focus', handleInteraction);
            window.addEventListener('load', handleInteraction);
            return () => {
                // Clean up event listeners
                document.removeEventListener('scroll', handleInteraction);
                document.removeEventListener('click', handleInteraction);
                document.removeEventListener('mousemove', handleInteraction);
                window.removeEventListener('focus', handleInteraction);
                window.removeEventListener('load', handleInteraction);
                if (cleanupIntervalRef.current) {
                    clearInterval(cleanupIntervalRef.current);
                    cleanupIntervalRef.current = null;
                }
                if (emergencyCleanupRef.current) {
                    clearInterval(emergencyCleanupRef.current);
                    emergencyCleanupRef.current = null;
                }
                if (scriptTimeoutRef.current) {
                    clearTimeout(scriptTimeoutRef.current);
                    scriptTimeoutRef.current = null;
                }
                if (mutationObserverRef.current) {
                    mutationObserverRef.current.disconnect();
                    mutationObserverRef.current = null;
                }
            };
        }
        // Remove premium marker if user is not premium
        if (!isAdFree && !isServerBlocked) {
            document.documentElement.removeAttribute('data-premium-user');
            document.documentElement.removeAttribute('data-k9-user');
            document.documentElement.removeAttribute('data-adsense-blocked');
            document.documentElement.removeAttribute('data-funding-choices-blocked');
        }
        
        // CRITICAL: For free users, ensure window.adsbygoogle and window.googlefc are NOT blocked
        // If they were blocked earlier (e.g., by race condition or stale state), try to restore them
        if (!isAdFree && !isServerBlocked) {
            // Check if window.adsbygoogle is blocked
            const adsbygoogleDescriptor = Object.getOwnPropertyDescriptor(window, 'adsbygoogle');
            const isAdsbygoogleBlocked = adsbygoogleDescriptor && 
                                        adsbygoogleDescriptor.configurable === false && 
                                        adsbygoogleDescriptor.value === undefined;
            
            if (isAdsbygoogleBlocked) {
                logger.error('[useGoogleAdSense] 🚨 CRITICAL: window.adsbygoogle is blocked with configurable:false for free user. This will prevent AdSense from loading. User may need to clear browser cache or refresh page.', {
                    isAdFree,
                    isServerBlocked,
                    descriptor: adsbygoogleDescriptor
                });
                // We can't unblock if configurable is false, but we can at least log the error
            } else if (adsbygoogleDescriptor?.configurable) {
                // Try to delete the property if it's configurable
                try {
                    delete (window as any).adsbygoogle;
                    logger.debug('[useGoogleAdSense] ✅ Successfully unblocked window.adsbygoogle for free user');
                } catch (e) {
                    logger.warn('[useGoogleAdSense] ⚠️ Error attempting to unblock window.adsbygoogle:', e);
                }
            }
            
            // Check if window.googlefc is blocked
            const googlefcDescriptor = Object.getOwnPropertyDescriptor(window, 'googlefc');
            const isGooglefcBlocked = googlefcDescriptor && googlefcDescriptor.configurable === false;
            
            if (isGooglefcBlocked) {
                logger.warn('[useGoogleAdSense] ⚠️ window.googlefc is blocked with configurable:false for free user - cannot unblock automatically.');
            } else if (googlefcDescriptor?.configurable) {
                // Try to restore googlefc
                try {
                    delete (window as any).googlefc;
                    (window as any).googlefc = {};
                    logger.debug('[useGoogleAdSense] ✅ Restored window.googlefc for free user');
                } catch (e) {
                    logger.warn('[useGoogleAdSense] ⚠️ Error attempting to restore window.googlefc:', e);
                }
            } else if (!googlefcDescriptor) {
                // googlefc doesn't exist yet, ensure it can be created
                (window as any).googlefc = {};
                logger.debug('[useGoogleAdSense] ✅ Initialized window.googlefc for free user');
            }
            
            // Log final state
            logger.debug('[useGoogleAdSense] Free user ad properties state', {
                isAdFree,
                isServerBlocked,
                adsbygoogleBlocked: isAdsbygoogleBlocked,
                googlefcBlocked: isGooglefcBlocked,
                adsbygoogleExists: !!window.adsbygoogle,
                googlefcExists: !!window.googlefc
            });
        }
        
        // CRITICAL: For guests (non-authenticated users), allow ads to load immediately
        // Guests are always free users, so we don't need to wait for premium status check
        const isGuest = !isAuthenticated && !isInitializing;
        
        // CRITICAL: Don't load ads while subscription status is loading OR hasn't been fetched yet
        // EXCEPT for guests - guests can load ads immediately since they're always free
        // This prevents race conditions where ads load before premium check completes for authenticated users
        if (!isGuest && isLoading) {
            logger.debug('[useGoogleAdSense] ⏳ Subscription status still loading - blocking ad load', {
                isLoading,
                isAdFree,
                isServerBlocked,
                isAuthenticated,
                isGuest
            });
            return;
        }
        
        // CRITICAL: For authenticated users, don't load ads if premium status hasn't been fetched yet
        // For guests, we can proceed since they're always free
        if (!isGuest && (premiumStatus === null || premiumStatus === undefined)) {
            logger.warn('[useGoogleAdSense] ⏳ Premium status not fetched yet - blocking ad load', {
                isLoading,
                isAdFree,
                isServerBlocked,
                isAuthenticated,
                isGuest,
                premiumStatus: premiumStatus ? JSON.stringify(premiumStatus) : 'null/undefined'
            });
            return;
        }
        
        // Log that we're proceeding to load ads for free users
        logger.info('[useGoogleAdSense] ✅ Proceeding to load AdSense', {
            isAdFree,
            isServerBlocked,
            isLoading,
            isAuthenticated,
            isGuest,
            premiumStatusSource: premiumStatus?.source,
            premiumStatusTier: premiumStatus?.subscriptionTier
        });
        
        // CRITICAL: If premiumStatus has source: 'none' and hasPremium is false,
        // it means the status was fetched before authentication completed (or for an unauthenticated user/guest).
        // For guests, we can load ads immediately. For authenticated users, only block if auth is still initializing.
        if (!isGuest && premiumStatus?.source === 'none' && !premiumStatus?.hasPremium) {
            // Only block if auth is still initializing - don't block just because user is authenticated
            if (isInitializing || isLoading) {
                logger.auth('[useGoogleAdSense] ⏳ Premium status has source: none - blocking ad load (waiting for auth/status to complete)', { "arg0": {
                        isLoading,
                        isAdFree,
                        isAuthenticated,
                        isInitializing,
                        premiumStatusSource: premiumStatus?.source,
                        premiumStatus: JSON.stringify(premiumStatus)
                    } });
                return;
            }
            // If auth is complete and status is loaded, source: 'none' with hasPremium: false means free user
            // Allow ads to load in this case
        }
        // CRITICAL: Block loading if premium user (from any source)
        if (isAdFree || isServerBlocked) {
            logger.debug('[useGoogleAdSense] 🚫 Premium user detected - blocking ad load', { "arg0": {
                    isAdFree,
                    isServerBlocked,
                    premiumStatus: premiumStatus ? JSON.stringify(premiumStatus) : 'not loaded'
                } });
            return;
        }

        // CRITICAL: Check for bots and headless browsers (SEO-SAFE - search engine bots are allowed)
        const botDetection = getCachedDetection();
        if (botDetection.shouldBlockAds) {
            logger.warn('[useGoogleAdSense] 🚫 Bot/Headless browser detected - blocking ad load', {
                isBot: botDetection.isBot,
                isHeadless: botDetection.isHeadless,
                isSearchEngineBot: botDetection.isSearchEngineBot,
                confidence: botDetection.confidence,
                shouldBlockAds: botDetection.shouldBlockAds,
                shouldBlockCompletely: botDetection.shouldBlockCompletely,
                userAgent: navigator.userAgent.substring(0, 100)
            });

            // Set data attribute for server-side coordination
            document.documentElement.setAttribute('data-bot-detected', 'true');

            // For very high confidence detections, completely block and remove any existing ads
            // NOTE: Bot detection is separate from premium user logic - bots should not see ads regardless of account type
            if (botDetection.shouldBlockCompletely) {
                document.documentElement.setAttribute('data-bot-completely-blocked', 'true');
                // Force ad removal for bots (even if not premium)
                removeAllAds(true); // Force removal for bots

                // Set up periodic cleanup for bots (more aggressive than premium users)
                cleanupIntervalRef.current = setInterval(() => {
                    const removed = removeAllAds(true); // Force removal for bots
                    if (removed > 0) {
                        logger.warn('[useGoogleAdSense] ⚠️ Removed ads that appeared despite bot blocking', { removed });
                    }
                }, 500); // Check every 500ms for bots
            }

            return;
        }

        // Log when legitimate non-bot traffic is detected
        if (botDetection.isBot && !botDetection.shouldBlockAds) {
            logger.info('[useGoogleAdSense] ✅ Legitimate search engine bot detected - allowing access', {
                botType: botDetection.details.userAgent.botType,
                confidence: botDetection.confidence
            });
        }
        if (!isPageLoaded || !isIdleReady) {
            logger.debug('[useGoogleAdSense] ⏳ Delaying AdSense until post-load idle window', {
                isPageLoaded,
                isIdleReady
            });
            return;
        }

        // Prevent multiple simultaneous load attempts
        if (loadingRef.current) {
            logger.api('[useGoogleAdSense] ⏳ AdSense script already loading, skipping duplicate request', "GET");
            return;
        }
        // Only load AdSense for confirmed non-premium users
        // Dedup by stable id and by existing adsbygoogle.js src (server legacy / race)
        const existingScript = (document.getElementById(ADSENSE_SCRIPT_ID) ||
            document.querySelector('script[src*="adsbygoogle.js"]')) as HTMLScriptElement | null;
        if (existingScript) {
            if (!existingScript.id) {
                existingScript.id = ADSENSE_SCRIPT_ID;
            }
            logger.debug('[useGoogleAdSense] ✅ AdSense script already loaded');
            if (isViteAdSenseSuppressGoogleOverlaysEnabled(import.meta.env.VITE_ADSENSE_SUPPRESS_GOOGLE_OVERLAYS)) {
                suppressGoogleOverlayAdsOnce();
            }
            return;
        }
        // Check if script was blocked for premium users
        if (document.documentElement.getAttribute('data-premium-blocked') === 'true') {
            logger.debug('[useGoogleAdSense] 🚫 AdSense blocked for premium user - not loading');
            return;
        }
        // Set loading flag
        loadingRef.current = true;
        logger.info('[useGoogleAdSense] 📥 Loading Google AdSense for non-premium user...', {
            isAdFree,
            isServerBlocked,
            premiumStatus: premiumStatus ? JSON.stringify(premiumStatus) : 'not loaded',
            scriptId: ADSENSE_SCRIPT_ID,
            clientId: ADSENSE_CLIENT_ID
        });
        // Create and inject the AdSense script
        const script = document.createElement('script');
        script.id = ADSENSE_SCRIPT_ID;
        script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT_ID}`;
        script.async = true;
        script.crossOrigin = 'anonymous';
        // Timeout protection - remove script if it takes too long to load
        scriptTimeoutRef.current = setTimeout(() => {
            if (loadingRef.current) {
                logger.warn('[useGoogleAdSense] ⚠️ AdSense script load timeout - removing script');
                script.remove();
                loadingRef.current = false;
                // Mark as failed to prevent retry loops
                document.documentElement.setAttribute('data-adsense-timeout', 'true');
            }
        }, SCRIPT_LOAD_TIMEOUT);
        script.onload = () => {
            logger.debug('[useGoogleAdSense] ✅ Google AdSense loaded for non-premium user');
            loadingRef.current = false;
            // Clear timeout on successful load
            if (scriptTimeoutRef.current) {
                clearTimeout(scriptTimeoutRef.current);
                scriptTimeoutRef.current = null;
            }
            if (isViteAdSenseSuppressGoogleOverlaysEnabled(import.meta.env.VITE_ADSENSE_SUPPRESS_GOOGLE_OVERLAYS)) {
                suppressGoogleOverlayAdsOnce();
            }
        };
        script.onerror = (error) => {
            logger.error('[useGoogleAdSense] ❌ Failed to load Google AdSense script:', error, {});
            loadingRef.current = false;
            script.remove();
            // Clear timeout on error
            if (scriptTimeoutRef.current) {
                clearTimeout(scriptTimeoutRef.current);
                scriptTimeoutRef.current = null;
            }
            // Mark as failed to prevent retry loops
            document.documentElement.setAttribute('data-adsense-failed', 'true');
        };
        try {
            document.head.appendChild(script);
            logger.debug('[useGoogleAdSense] 📤 AdSense script appended to head');
        }
        catch (error) {
            logger.error('[useGoogleAdSense] ❌ Error appending AdSense script:', error, {});
            loadingRef.current = false;
            if (scriptTimeoutRef.current) {
                clearTimeout(scriptTimeoutRef.current);
                scriptTimeoutRef.current = null;
            }
        }
        // Cleanup function
        return () => {
            // Don't remove script on unmount to prevent flickering
            // It will be removed if user upgrades to premium
            if (cleanupIntervalRef.current) {
                clearInterval(cleanupIntervalRef.current);
                cleanupIntervalRef.current = null;
            }
            if (emergencyCleanupRef.current) {
                clearInterval(emergencyCleanupRef.current);
                emergencyCleanupRef.current = null;
            }
            if (scriptTimeoutRef.current) {
                clearTimeout(scriptTimeoutRef.current);
                scriptTimeoutRef.current = null;
            }
            if (mutationObserverRef.current) {
                mutationObserverRef.current.disconnect();
                mutationObserverRef.current = null;
            }
        };
    }, [isAdFree, isLoading, isServerBlocked, premiumStatus, isPageLoaded, isIdleReady, isAuthenticated, isInitializing]);
    return {
        adsenseLoaded: !isAdFree && !isLoading,
        shouldShowAds: !isAdFree,
    };
};
export default useGoogleAdSense;
